<script setup lang="ts">
import Down from '@/assets/icon/down.svg'
import Up from '@/assets/icon/up.svg'
import BtcIcon from '@/assets/img/exchange-detail/exchange-funds/btc.png'
import EthIcon from '@/assets/img/exchange-detail/exchange-funds/eth.png'
import { getOfficialTokenCurrencyList, type OfficialTokenCurrencyListRow } from '~/api/officialtoken'
import { getAliasResolve, getTraderDetail } from '~/server/detail'
const { t } = useAppI18n()
type MetricItem = {
  label: string
  value: string
  reverse?: boolean
  compact?: boolean
}

type CurrencyRow = {
  code: string
  name: string
  pair: string
  price: string
  change: string
  changeType: number
  changeColor: 'up' | 'down'
  volume: string
  depth: string
  liquidity: string
  logoSrc?: string
  logoText?: string
  logoClass?: string
}

const route = useRoute()

const PAGE_SIZE = 50
const DEFAULT_HEADERS = ['币种/名称', '交易对', '单价', '涨幅', '24H交易量', '+2% / -2% 市场深度', '流动性']

const normalizeText = (value: unknown) => {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value === 'object') {
    const textValue = value as Record<string, unknown>

    return (
      normalizeText(textValue.showName) ||
      normalizeText(textValue.name) ||
      normalizeText(textValue.exchangeShortName) ||
      normalizeText(textValue.shortName) ||
      normalizeText(textValue.localShortName) ||
      normalizeText(textValue.shortNameLocal) ||
      normalizeText(textValue.localFullName) ||
      normalizeText(textValue.fullName)
    )
  }

  return String(value).trim()
}

const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return normalizeText(value[0])
  }

  return normalizeText(value)
}

const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))
const exchangeName = computed(() => {
  return exchangeSlug.value
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
})
const routePage = computed(() => {
  const page = Number(getFirstRouteValue(route.params.page) || 1)

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
})

const { data: aliasResolveData } = await useAsyncData(
  () => `alias-resolve-${exchangeSlug.value}`,
  () => getAliasResolve({ alias: exchangeSlug.value }),
)

const traderCode = computed(() => {
  const resolvedData = aliasResolveData.value?.data as Record<string, any>
  const items = resolvedData?.items
  const resolvedCode = normalizeText(items?.find((item: any) => item.platform === 2)?.traderCode)

  return resolvedCode || exchangeSlug.value
})

const { data: traderDetailData } = await useAsyncData(
  () => `officialtoken-currency-trader-detail-${traderCode.value}`,
  async () => {
    if (!traderCode.value) {
      return null
    }

    return await getTraderDetail(traderCode.value)
  },
  {
    watch: [traderCode],
  },
)

const currentPage = ref(routePage.value)

watch(
  [traderCode, routePage],
  () => {
    currentPage.value = routePage.value
  },
  { immediate: true },
)

const fallbackIcons: Record<string, string> = {
  BTC: BtcIcon,
  ETH: EthIcon,
}

const getLogoMeta = (item: OfficialTokenCurrencyListRow) => {
  const code = normalizeText(item.code).toUpperCase()
  const logoSrc = normalizeText(item.icon) || fallbackIcons[code]

  if (logoSrc) {
    return {
      logoSrc,
      logoText: '',
      logoClass: 'officialtoken-detail__logo-image-wrap',
    }
  }

  return {
    logoSrc: '',
    logoText: code.slice(0, 1) || '?',
    logoClass: 'officialtoken-detail__logo-fallback',
  }
}

const { data: currencyListData } = await useAsyncData(
  () => `officialtoken-currency-list-${traderCode.value}-${currentPage.value}`,
  async () => {
    if (!traderCode.value) {
      return null
    }

    return await getOfficialTokenCurrencyList({
      exchangeId: traderCode.value,
      page: currentPage.value,
      size: PAGE_SIZE,
    })
  },
  {
    watch: [traderCode, currentPage],
  },
)

const cardTitle = computed(() => normalizeText(currencyListData.value?.title) || '支持币种')
const breadcrumbExchangeName = computed(
  () =>
    normalizeText(traderDetailData.value?.basicInfo?.showName) ||
    normalizeText(traderDetailData.value?.traderData?.basicData?.showName) ||
    exchangeName.value ||
    traderCode.value,
)

/** 页面面包屑：首页 -> 交易所详情 -> 当前支持币种列表。 */
const breadcrumbItems = computed(() => [
  {
    label: '首页',
    route: '/',
  },
  {
    label: breadcrumbExchangeName.value,
    route: {
      name: 'exchange-detail',
      params: {
        slug: exchangeSlug.value,
      },
    },
  },
  {
    label: cardTitle.value,
  },
])

const metrics = computed<MetricItem[]>(() => [
  { value: normalizeText(currencyListData.value?.coinCount) || '--', label: '币种' },
  { value: normalizeText(currencyListData.value?.pairCount) || '--', label: '交易对' },
  {
    label: '流动性',
    value: normalizeText(currencyListData.value?.liquidityRank) || '--',
    reverse: true,
    compact: true,
  },
])

const rows = computed<CurrencyRow[]>(() => {
  return (currencyListData.value?.list || []).map((item) => {
    const logoMeta = getLogoMeta(item)
    const change = normalizeText(item.change)

    return {
      code: normalizeText(item.code) || '--',
      name: normalizeText(item.name) || '--',
      pair: normalizeText(item.pair) || '--',
      price: normalizeText(item.price) || '--',
      change: change || '--',
      changeType: Number(item.changeType || 0),
      changeColor: item.changeType === 1 ? 'up' : 'down',
      volume: normalizeText(item.volume24h) || '--',
      depth: normalizeText(item.marketDepth) || '--',
      liquidity: normalizeText(item.liquidity) || '--',
      ...logoMeta,
    }
  })
})

const totalPages = computed(() => {
  const pages = Number(currencyListData.value?.totalPages || 0)
  const total = Number(currencyListData.value?.total || 0)

  if (pages > 0) {
    return pages
  }

  return total > 0 ? Math.ceil(total / PAGE_SIZE) : 1
})

/** 公共分页组件以总条数计算页数；接口仅返回总页数时按页大小补齐。 */
const paginationTotal = computed(() => {
  const total = Number(currencyListData.value?.total || 0)

  return total > 0 ? total : totalPages.value * PAGE_SIZE
})

/** 支持币种分页的统一路由模板，第一页会由公共组件自动还原为列表首页。 */
const currencyPageRoute = computed(() => `/exchanges/${exchangeSlug.value}/coins/page/{idx}`)
</script>

<template>
  <section class="officialtoken-page">
    <div class="officialtoken-page__container">
      <CommonBreadcrumb :items="breadcrumbItems" />

      <section class="officialtoken-detail" aria-labelledby="officialtoken-detail-title">
        <div class="officialtoken-detail__header">
          <div class="officialtoken-detail__title-row">
            <h2 id="officialtoken-detail-title" class="officialtoken-detail__title">{{ cardTitle }}</h2>
          </div>

          <div class="officialtoken-detail__metrics" aria-label="支持币种概览">
            <div
              v-for="metric in metrics"
              :key="`${metric.label}-${metric.value}`"
              class="officialtoken-detail__metric"
              :class="{
                'officialtoken-detail__metric-reverse': metric.reverse,
                'officialtoken-detail__metric-compact': metric.compact,
              }"
            >
              <template v-if="metric.reverse">
                <span class="officialtoken-detail__metric-label">{{ metric.label }}</span>
                <span class="officialtoken-detail__metric-value">{{ metric.value }}</span>
              </template>
              <template v-else>
                <span class="officialtoken-detail__metric-value">{{ metric.value }}</span>
                <span class="officialtoken-detail__metric-label">{{ metric.label }}</span>
              </template>
            </div>
          </div>
        </div>

        <div class="officialtoken-detail__table-wrap">
          <table class="officialtoken-detail__table">
            <colgroup>
              <col class="officialtoken-detail__col-coin" />
              <col class="officialtoken-detail__col-pair" />
              <col class="officialtoken-detail__col-price" />
              <col class="officialtoken-detail__col-change" />
              <col class="officialtoken-detail__col-volume" />
              <col class="officialtoken-detail__col-depth" />
              <col class="officialtoken-detail__col-liquidity" />
            </colgroup>
            <thead>
              <tr>
                <th
                  v-for="(header, index) in DEFAULT_HEADERS"
                  :key="header"
                  :class="{ 'officialtoken-detail__table-th-right': index === 6 }"
                >
                  {{ header }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in rows" :key="`${item.code}-${item.name}-${item.pair}`">
                <td>
                  <div class="officialtoken-detail__coin">
                    <span
                      class="officialtoken-detail__logo"
                      :class="item.logoClass || 'officialtoken-detail__logo-fallback'"
                    >
                      <CommonImage
                        v-if="item.logoSrc"
                        :src="item.logoSrc"
                        :alt="item.name"
                        class="officialtoken-detail__logo-image"
                      />
                      <span v-else>{{ item.logoText }}</span>
                    </span>
                    <span class="officialtoken-detail__coin-name">{{ item.name }}</span>
                  </div>
                </td>
                <td>{{ item.pair }}</td>
                <td>{{ item.price }}</td>
                <td
                  class="officialtoken-detail__change"
                  :class="{
                    'officialtoken-detail__change-up': item.changeColor === 'up',
                    'officialtoken-detail__change-down': item.changeColor === 'down',
                  }"
                >
                  <span class="officialtoken-detail__change-content">
                    <span>{{ item.change }}</span>
                    <Up
                      v-if="item.changeType === 1"
                      class="change-icon officialtoken-detail__change-icon officialtoken-detail__change-icon-up"
                      aria-hidden="true"
                    />
                    <Down
                      v-else-if="item.changeType === 2"
                      class="change-icon officialtoken-detail__change-icon officialtoken-detail__change-icon-down"
                      aria-hidden="true"
                    />
                  </span>
                </td>
                <td>{{ item.volume }}</td>
                <td class="officialtoken-detail__depth">{{ item.depth }}</td>
                <td class="officialtoken-detail__table-td-right">{{ item.liquidity }}</td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="7" class="officialtoken-detail__empty">{{ t('83714') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <nav
          v-if="totalPages > 1"
          class="officialtoken-detail__pagination"
          aria-label="supported currencies pagination"
        >
          <CommonPagination
            :current-page="currentPage"
            :total="paginationTotal"
            :page-size="PAGE_SIZE"
            :page-route="currencyPageRoute"
          />
        </nav>
      </section>
    </div>
  </section>
</template>

<style scoped lang="less">
.officialtoken-page {
  position: relative;
  padding: 70px 0 120px;
  background: #fff;
}

.officialtoken-page__container {
  width: 1200px;
  margin: 0 auto;
  font-family: Outfit, 'PingFang SC', sans-serif;
}

.officialtoken-detail__title,
.officialtoken-detail__metric-label,
.officialtoken-detail__metric-value,
.officialtoken-detail__coin-name {
  margin: 0;
}

.officialtoken-detail {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 21px;
  width: 100%;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
}

.officialtoken-detail__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 21px;
  width: 100%;
}

.officialtoken-detail__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 16px;
}

.officialtoken-detail__title {
  color: #0f1728;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
}

.officialtoken-detail__metrics {
  display: flex;
  align-items: center;
  gap: 20px;
}

.officialtoken-detail__metric {
  display: flex;
  align-items: baseline;
  gap: 6px;
  color: #0f1728;
}

.officialtoken-detail__metric-value {
  color: #0f1728;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.officialtoken-detail__metric-label {
  color: #9098a9;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.officialtoken-detail__metric-compact {
  align-items: center;
  gap: 6px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 2px;
  padding: 4px 8px;
  background: #fff;
}

.officialtoken-detail__metric-compact .officialtoken-detail__metric-value {
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
}

.officialtoken-detail__table-wrap {
  width: 100%;
}

.officialtoken-detail__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.officialtoken-detail__col-coin {
  width: 230px;
}

.officialtoken-detail__col-pair,
.officialtoken-detail__col-price,
.officialtoken-detail__col-change,
.officialtoken-detail__col-volume,
.officialtoken-detail__col-liquidity {
  width: 148px;
}

.officialtoken-detail__col-depth {
  width: 230px;
}

.officialtoken-detail__table thead th {
  padding: 0 32px 14px 0;
  border-bottom: 1px solid #eef2f6;
  color: #a8b0bd;
  text-align: right;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;

  &:first-child {
    text-align: left;
  }
}

.officialtoken-detail__table tbody td {
  padding: 16px 32px 16px 0;
  border-bottom: 1px solid #f2f5f8;
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  vertical-align: middle;
  text-align: right;

  &:first-child {
    text-align: left;
  }
}

.officialtoken-detail__table thead th:last-child,
.officialtoken-detail__table tbody td:last-child {
  padding-right: 0;
}

.officialtoken-detail__table tbody tr:last-child td {
  border-bottom: none;
}

.officialtoken-detail__coin {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.officialtoken-detail__logo {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.officialtoken-detail__logo-image-wrap {
  background: #ffffff;
}

.officialtoken-detail__logo-image {
  display: block;
  width: 28px;
  height: 28px;
}

.officialtoken-detail__logo-fallback {
  background: #f1f4fb;
  color: #1f3f93;
}

.officialtoken-detail__coin-name {
  overflow: hidden;
  color: #1a2233;
  font-size: 16px;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.officialtoken-detail__change {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.officialtoken-detail__change-content {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.officialtoken-detail__change-icon {
  width: 12px;
  height: 12px;
  flex: none;
}

.change-icon {
  flex: none;
  transform-origin: center;
  will-change: transform, opacity;
}

.officialtoken-detail__change-up {
  color: #16a085 !important;
}

.officialtoken-detail__change-down {
  color: #eb5757 !important;
}

.officialtoken-detail__change-icon-up {
  animation: exchange-supported-currencies-rise 1.5s ease-in-out infinite;
}

.officialtoken-detail__change-icon-down {
  animation: exchange-supported-currencies-fall 1.5s ease-in-out infinite;
}

.officialtoken-detail__depth {
  color: #202938;
}

.officialtoken-detail__table-th-right,
.officialtoken-detail__table-td-right {
  text-align: right;
}

.officialtoken-detail__empty {
  color: #9098a9;
  text-align: center !important;
}

.officialtoken-detail__pagination {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 16px 0 8px;
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .officialtoken-page {
    padding: 120px 0 96px;
  }

  .officialtoken-page__container {
    width: calc(100% - 48px);
  }

  .officialtoken-detail {
    gap: 18px;
  }

  .officialtoken-detail__header {
    gap: 18px;
  }

  .officialtoken-detail__table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .officialtoken-detail__table {
    min-width: 1200px;
  }
}

@media screen and (max-width: 799px) {
  .officialtoken-page {
    padding: 120px 0 88px;
  }

  .officialtoken-page__container {
    width: calc(816px - 48px);
  }

  .officialtoken-detail {
    gap: 18px;
  }

  .officialtoken-detail__header {
    gap: 18px;
  }

  .officialtoken-detail__table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .officialtoken-detail__table {
    min-width: 1200px;
  }
}

@keyframes exchange-supported-currencies-rise {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(-2px);
    opacity: 0.85;
  }
}

@keyframes exchange-supported-currencies-fall {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(2px);
    opacity: 0.85;
  }
}
</style>
